import React, {MouseEvent, useCallback, useEffect, useLayoutEffect, useRef, useState} from 'react';
import cx from 'classnames';
import {
    Card,
    getDirectionByText,
    Icon,
    noop,
    PriorityIcon,
    Text,
    Title,
    ToggleSwitch
} from '@priority-software/priority-style-react';
import {BusinessRuleAction, businessRulesActionMap, overviewDialogID} from '../../../../constants';
import {ModernTooltip} from '../../../../../../components/Common/Tooltip/ModernTooltip';
import {highlightText} from '../../../../../../_legacy/library/utils/highlightText';
import {createDataTestId} from '../../../../../../utils/dataTestId';
import {automationOverviewListItemID} from '../../../../testIDs';
import {isRTL} from '../../../../../../Localization';
import {RuleType} from '../../../../enums';
import {AlertListItem} from '../../../../reducer/types';
import {getInputDirection} from '../../../../../Form/common/getInputDirections';
import {BusinessRuleListDoubleclick, BusinessRuleListEditIcon} from '../../consts';
import {sendMonitor} from '../../../../../../utils/monitoring';
import {texts} from '../../../../texts';
import styles from '../../styles.module.scss';

type Props = {
    item: AlertListItem;
    onEdit: (ruleNumber: number, fetchHtml?: boolean) => void;
    onRemove: (ruleNumber: number) => void;
    onCopy: (ruleNumber: number) => void;
    onClick: (ruleNumber: number) => void;
    handleRuleActive: (
        ruleNumber: number,
        isActive: boolean,
        callback?: () => void
    ) => Promise<void>;
    isFocused: boolean;
    searchText: string;
    componentClasses?: string;
    ruleType: RuleType;
};

const direction = getInputDirection();

export const OverviewCard = (props: Props) => {
    const {
        item,
        onEdit,
        onRemove,
        onCopy,
        onClick,
        handleRuleActive,
        isFocused,
        searchText,
        componentClasses,
        ruleType
    } = props;
    const [isActive, setIsActive] = useState(item.isActive);
    const [isTruncated, setIsTruncated] = useState(false);
    const [isExpanded, setIsExpanded] = useState(false);

    const conditionBlockRef = useRef<HTMLDivElement>();
    const cardRef = useRef<HTMLDivElement>();

    const ruleNumber = item.alertNumber;
    const ruleAction = item.ruleAction;
    const hasSearchQuery = !!searchText;

    useEffect(() => {
        function handleTruncated() {
            const el = conditionBlockRef.current;
            if (el) {
                requestAnimationFrame(() => {
                    setIsTruncated(el.scrollWidth > el.clientWidth);
                    if (hasSearchQuery) {
                        setIsExpanded(true);
                    }
                });
            }
        }

        handleTruncated();
    }, [hasSearchQuery]);

    useLayoutEffect(() => {
        const cardEl = cardRef.current;

        if (isFocused && cardEl) {
            requestAnimationFrame(() => {
                cardEl.scrollIntoView({
                    block: 'nearest',
                    inline: 'nearest'
                });
            });

            const controls = [
                cardRef.current.querySelector('[role="switch"]'),
                ...cardRef.current.getElementsByClassName(styles.iconButton)
            ].filter(Boolean);

            function getNextEl() {
                const index = controls.findIndex(el => el === document.activeElement);
                return controls[Math.min(controls.length - 1, index + 1)];
            }

            function getPrevElement() {
                const index = controls.findIndex(el => el === document.activeElement);
                return controls[Math.max(0, index - 1)];
            }

            function onKeydown(e: KeyboardEvent) {
                if (e.key === 'ArrowLeft') {
                    const focusEl = isRTL() ? getNextEl() : getPrevElement();
                    (focusEl as HTMLButtonElement)?.focus();
                } else if (e.key === 'ArrowRight') {
                    const focusEl = isRTL() ? getPrevElement() : getNextEl();
                    (focusEl as HTMLButtonElement)?.focus();
                }
            }

            window.addEventListener('keydown', onKeydown);
            return () => window.removeEventListener('keydown', onKeydown);
        }
        return noop;
    }, [isFocused]);

    const toggleConditions = useCallback(e => {
        e.stopPropagation();
        setIsExpanded(state => !state);
    }, []);

    const handleActive = useCallback(async () => {
        const nextState = !isActive;

        await handleRuleActive(ruleNumber as number, nextState, () => setIsActive(nextState));
    }, [isActive, ruleNumber, handleRuleActive]);

    const handleRemove = useCallback(
        (e: MouseEvent<HTMLButtonElement>) => {
            e.preventDefault();
            onRemove(ruleNumber as number);
        },
        [onRemove, ruleNumber]
    );

    const handlePreEdit = (
        e: MouseEvent<HTMLButtonElement | HTMLDivElement>,
        isDoubleClicked: boolean
    ) => {
        if (ruleType === RuleType.business) {
            const monitorTransactionText = isDoubleClicked
                ? BusinessRuleListDoubleclick
                : BusinessRuleListEditIcon;
            sendMonitor(monitorTransactionText);
        }
        handleEdit(e);
    };

    const handleEdit = useCallback(
        (e: MouseEvent<HTMLButtonElement | HTMLDivElement>) => {
            e.stopPropagation();
            onEdit(ruleNumber as number, ruleAction === BusinessRuleAction.EmailMsg);
        },
        [onEdit, ruleNumber, ruleAction]
    );

    const handleCopy = useCallback(
        (e: MouseEvent<HTMLButtonElement>) => {
            e.preventDefault();
            e.stopPropagation();
            onCopy(ruleNumber as number);
        },
        [onCopy, ruleNumber]
    );

    const handleClick = useCallback(() => {
        onClick(item.alertNumber);
    }, [onClick, item.alertNumber]);

    const actionData = businessRulesActionMap[Number(item.ruleAction)];

    const isBusinessRule = ruleType === RuleType.business;
    const isBPM = ruleType === RuleType.bpm;

    const renderAutofillCondition = () => {
        const parts = isRTL() ? (
            <>
                <span key="1">{texts.in_label}</span>
                <span key="2">&nbsp;</span>
                <span key="3">
                    {highlightText(`"${item.autofillData?.targetField}"` || '', searchText)}
                </span>
            </>
        ) : (
            <>
                <span key="1">{texts.in_label}</span>
                <span key="2">&nbsp;</span>
                <span key="3">
                    {highlightText(`"${item.autofillData?.targetField}"` || '', searchText)}
                </span>
                <span key="4">&nbsp;</span>
                <span key="5">{texts.autofill_card_field_label}</span>
            </>
        );

        return (
            <>
                <Text
                    tag="span"
                    skin="paragraph-1"
                    textColor="secondary-400"
                    componentClasses={styles.operator}
                >
                    {texts.set_label}
                </Text>

                <Text tag="span" skin="paragraph-1" fontWeight="medium" textColor="primary-700">
                    <span dir={getDirectionByText(item.autofillData?.autofillValue, direction)}>
                        ({highlightText(item.autofillData?.autofillValue || '', searchText)})
                    </span>{' '}
                    {parts}
                </Text>
            </>
        );
    };

    const isLocked = false;

    return (
        <div
            ref={cardRef}
            onDoubleClick={!isLocked ? e => handlePreEdit(e, true) : noop}
            onClick={handleClick}
            data-ruleid={ruleNumber}
            data-focused={isFocused}
            {...createDataTestId(automationOverviewListItemID)}
        >
            <Card
                componentClasses={cx(styles.ruleItem, componentClasses, {
                    [styles.focused]: isFocused
                })}
            >
                <div className={styles.ruleContent}>
                    <div
                        className={styles.conditionBlock}
                        {...createDataTestId('condition-block', false)}
                    >
                        <Title skin="h5" textColor="primary-700" text={texts.when_label} />

                        <div>
                            <div
                                ref={conditionBlockRef}
                                className={cx(styles.conditions, {[styles.expanded]: isExpanded})}
                                {...createDataTestId('condition-text', false)}
                            >
                                {ruleType === RuleType.autofill && (
                                    <>
                                        <Text
                                            tag="span"
                                            skin="paragraph-1"
                                            fontWeight="medium"
                                            textColor="primary-700"
                                        >
                                            {highlightText(
                                                item.autofillData.sourceField,
                                                searchText
                                            )}
                                        </Text>

                                        <Text
                                            tag="span"
                                            skin="paragraph-1"
                                            fontWeight="normal"
                                            textColor="primary-700"
                                        >
                                            {highlightText(item.autofillData.action, searchText)}
                                        </Text>

                                        {item.conditions.length > 0 && (
                                            <Text
                                                tag="span"
                                                skin="paragraph-1"
                                                textColor="secondary-400"
                                                componentClasses={styles.operator}
                                            >
                                                {texts.operator_and_label.toLocaleLowerCase()}
                                            </Text>
                                        )}
                                    </>
                                )}
                                {item.conditions.map((condition, index) => {
                                    return (
                                        <React.Fragment key={index}>
                                            <Text
                                                tag="span"
                                                skin="paragraph-1"
                                                fontWeight="medium"
                                                textColor="primary-700"
                                            >
                                                {highlightText(
                                                    `${condition.conditionField}`,
                                                    searchText
                                                )}
                                            </Text>

                                            <Text
                                                tag="span"
                                                skin="paragraph-1"
                                                fontWeight="normal"
                                                textColor="primary-700"
                                            >
                                                {highlightText(
                                                    `${condition.conditionOperator}`,
                                                    searchText
                                                )}
                                            </Text>

                                            {condition.conditionValue && (
                                                <Text
                                                    tag="span"
                                                    skin="paragraph-1"
                                                    fontWeight="medium"
                                                    textColor="primary-700"
                                                >
                                                    <span
                                                        dir={getDirectionByText(
                                                            condition.conditionValue,
                                                            direction
                                                        )}
                                                    >
                                                        {highlightText(
                                                            condition.conditionValue,
                                                            searchText
                                                        )}
                                                    </span>
                                                </Text>
                                            )}

                                            {condition.conditionSecondValue && (
                                                <>
                                                    <Text
                                                        tag="span"
                                                        skin="paragraph-1"
                                                        textColor="secondary-400"
                                                        componentClasses={styles.operator}
                                                    >
                                                        {texts.falls_between_and_operator_label.toLocaleLowerCase()}
                                                    </Text>

                                                    <Text
                                                        tag="span"
                                                        skin="paragraph-1"
                                                        fontWeight="medium"
                                                        textColor="primary-700"
                                                    >
                                                        <span
                                                            dir={getDirectionByText(
                                                                condition.conditionSecondValue,
                                                                direction
                                                            )}
                                                        >
                                                            {highlightText(
                                                                condition.conditionSecondValue,
                                                                searchText
                                                            )}
                                                        </span>
                                                    </Text>
                                                </>
                                            )}

                                            {index + 1 < item.conditions.length && (
                                                <Text
                                                    tag="span"
                                                    skin="paragraph-1"
                                                    textColor="secondary-400"
                                                    componentClasses={styles.operator}
                                                >
                                                    {item.conditionOperator.toLocaleLowerCase()}
                                                </Text>
                                            )}
                                        </React.Fragment>
                                    );
                                })}
                            </div>

                            {isTruncated && (
                                <Text
                                    tag="div"
                                    textColor="primary-500"
                                    skin="paragraph-2"
                                    fontWeight="normal"
                                    onClick={toggleConditions}
                                    componentClasses={styles.showMoreBtn}
                                >
                                    {isExpanded ? texts.show_less_label : texts.show_more_label}
                                </Text>
                            )}
                        </div>
                    </div>

                    <div
                        className={styles.actionBlock}
                        {...createDataTestId('action-block', false)}
                    >
                        <Title skin="h5" textColor="primary-700" text={texts.then_label} />
                        <Text
                            tag="div"
                            textColor="primary-700"
                            skin="paragraph-1"
                            componentClasses={styles.actionText}
                            {...createDataTestId('rule-text', true)}
                        >
                            {isBusinessRule || isBPM ? (
                                <>
                                    {actionData?.icon && <Icon icon={actionData.icon} />}
                                    {highlightText(
                                        item.ruleActionText || actionData?.title,
                                        searchText
                                    )}
                                </>
                            ) : (
                                renderAutofillCondition()
                            )}
                        </Text>
                    </div>
                </div>

                <footer className={styles.ruleFooter}>
                    <div className={styles.partLeft}>
                        <div className={styles.gridInfo}>
                            <div className={styles.gridInfoItem}>
                                <Text
                                    skin="caption-2"
                                    textColor="secondary-400"
                                    tag="div"
                                    {...createDataTestId('grid-info-item-title', true)}
                                >
                                    {texts.rule_number_label}
                                </Text>
                                <Text
                                    {...createDataTestId('grid-info-item-value', true)}
                                    componentClasses={styles.ellipsis}
                                    skin="paragraph-2"
                                    textColor="primary-700"
                                    tag="div"
                                >
                                    {highlightText(`${item.alertNumber}`, searchText)}
                                </Text>
                            </div>
                            <div className={styles.gridInfoItem}>
                                <Text
                                    skin="caption-2"
                                    textColor="secondary-400"
                                    tag="div"
                                    {...createDataTestId('grid-info-item-title', true)}
                                >
                                    {texts.rule_description_label}
                                </Text>
                                <Text
                                    componentClasses={styles.ellipsis}
                                    skin="paragraph-2"
                                    textColor="primary-700"
                                    tag="div"
                                    {...createDataTestId('grid-info-item-value', true)}
                                >
                                    {highlightText(item.description, searchText) || '-'}
                                </Text>
                            </div>
                            {ruleType !== RuleType.bpm ? (
                                <div className={styles.gridInfoItem}>
                                    <Text
                                        skin="caption-2"
                                        textColor="secondary-400"
                                        tag="div"
                                        {...createDataTestId('grid-info-item-title', true)}
                                    >
                                        {texts.rule_valid_for_label}
                                    </Text>
                                    <Text
                                        componentClasses={styles.ellipsis}
                                        skin="paragraph-2"
                                        textColor="primary-700"
                                        tag="div"
                                        {...createDataTestId('grid-info-item-value', true)}
                                    >
                                        <span title={item.validFor}>
                                            {highlightText(item.validFor, searchText)}
                                        </span>
                                    </Text>
                                </div>
                            ) : null}
                            <div className={styles.gridInfoItem}>
                                <Text
                                    skin="caption-2"
                                    textColor="secondary-400"
                                    tag="div"
                                    {...createDataTestId('grid-info-item-title', true)}
                                >
                                    {texts.updated_by_label}
                                </Text>
                                <Text
                                    componentClasses={styles.ellipsis}
                                    skin="paragraph-2"
                                    textColor="primary-700"
                                    tag="div"
                                    {...createDataTestId('grid-info-item-value', true)}
                                >
                                    {highlightText(item.updatedBy, searchText)}
                                </Text>
                            </div>
                        </div>
                    </div>

                    <div className={styles.partRight}>
                        <ModernTooltip
                            tooltip={isActive ? texts.is_active_tooltip : texts.is_inactive_tooltip}
                            portalId={overviewDialogID}
                            position="top"
                        >
                            <ToggleSwitch
                                disabled={isLocked}
                                checked={isActive}
                                onChange={handleActive}
                                componentClasses={styles.toggleSwitch}
                                id={`${item.alertNumber}`}
                                {...createDataTestId('active-inactive-toggle', false)}
                            />
                        </ModernTooltip>

                        <ModernTooltip
                            tooltip={texts.edit_tooltip}
                            portalId={overviewDialogID}
                            position="top"
                        >
                            <button
                                className={styles.iconButton}
                                onClick={!isLocked ? e => handlePreEdit(e, false) : noop}
                                disabled={isLocked}
                                type="button"
                            >
                                <Icon disabled={isLocked} icon={PriorityIcon.editFill} hoverable />
                            </button>
                        </ModernTooltip>

                        <ModernTooltip
                            tooltip={texts.duplicate_tooltip}
                            portalId={overviewDialogID}
                            position="top"
                        >
                            <button
                                className={cx(styles.iconButton)}
                                onClick={handleCopy}
                                disabled={isLocked}
                                type="button"
                            >
                                <Icon disabled={isLocked} icon={PriorityIcon.duplicate} hoverable />
                            </button>
                        </ModernTooltip>

                        <ModernTooltip
                            tooltip={texts.delete_tooltip}
                            portalId={overviewDialogID}
                            position="top"
                        >
                            <button
                                className={cx(styles.iconButton)}
                                onClick={handleRemove}
                                disabled={isLocked}
                                type="button"
                            >
                                <Icon
                                    icon={PriorityIcon.delete}
                                    iconColor="validation-red"
                                    disabled={isLocked}
                                    hoverable
                                />
                            </button>
                        </ModernTooltip>
                    </div>
                </footer>
            </Card>
        </div>
    );
};
